.ul-hero-header-type-1 {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  // The breadcrumbs bar sits directly above the hero and shares its dark band, so the hero takes
  // the rest of the viewport. The offsets are static on purpose: this used to be measured in JS
  // after mount and written inline, which moved the block on first paint. Fixed values ship in
  // the stylesheet, so the very first paint — server-rendered included — is already final.
  // The crumb list wraps on narrow screens, hence the larger mobile offset.
  min-height: calc(100dvh - 118px);

  @include desktop {
    min-height: calc(100dvh - 45px);
  }

  overflow: hidden;
  background-color: var(--color-surface-darkest);
  color: var(--text-color-alternate);
  text-align: center;

  // Background image — form preview at 20% opacity
  &__background {
    position: absolute;
    z-index: 0;
    pointer-events: none;

    // Mobile: full width, vertically centered
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;

    &::before {
      content: "";
      background: rgb(0 0 0 / 65%);
      inset: 0;
      position: absolute;
      width: 100%;
      z-index: 2;
    }

    // Desktop: top offset 87px from Figma, horizontally centered, 936px wide
    @include desktop {
      top: px-to-rem(87px);
      left: 50%;
      transform: translateX(-50%);
      width: px-to-rem(936px);
    }
  }

  &__background-img {
    display: block;
    width: 100%;
    height: auto;
    object-fit: contain;
  }

  // Main content — centered in the hero
  &__content {
    position: relative;
    z-index: 2;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: px-to-rem(936px);
    padding: var(--spacing-3xl) var(--spacing-xl);
    gap: var(--spacing-2xl);

    @include desktop {
      padding: var(--spacing-5xl) var(--spacing-2xl);
    }
  }

  // Heading: 32px mobile → 40px/48px desktop, white, bold
  me-heading[slot='heading'],
  me-heading[slot='heading'] .me-heading {
    font-size: px-to-rem(32px);
    font-weight: map-get($font-weights, bold);
    line-height: 1.2;
    color: var(--text-color-alternate);
    text-align: center;

    @include desktop {
      font-size: px-to-rem(40px);
      line-height: px-to-rem(48px);
    }
  }

  // Description: white, semi-transparent
  me-text[slot='description'] .me-text {
    font-size: px-to-rem(16px);
    line-height: 1.5;
    color: rgb(255 255 255 / 85%);
    text-align: center;
  }

  // CTA button: orange, 220px
  &__cta {
    display: block;
    width: px-to-rem(220px);

    .me-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      padding: px-to-rem(12px) px-to-rem(24px);
      font-size: px-to-rem(16px);
      font-weight: map-get($font-weights, semi-bold);
      line-height: 1.5;
      border-radius: var(--radius-size-sm);
      background-color: var(--action-color-brand-primary);
      border: 1px solid var(--action-color-brand-primary);
      color: var(--text-color-alternate);
      cursor: pointer;
      box-sizing: border-box;
    }
  }

  // Details block — pinned to bottom, grows UPWARD on expand
  // bottom: 0 is fixed; as panel opens, height increases → top edge moves up
  &__details {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 3;
    background-color: #22222b; // Figma exact value
  }

  // Expandable panel — hidden by default, grid 0fr→1fr animation
  &__details-panel {
    display: grid;
    grid-template-rows: 0fr;

    // overflow: hidden on the panel is essential for the 0fr collapse to work
    overflow: hidden;
    transition: grid-template-rows 0.4s cubic-bezier(0.4, 0, 0.2, 1);

    &--open {
      grid-template-rows: 1fr;
    }
  }

  // Direct grid child — NO padding here (padding on inner child causes 0fr to not fully collapse)
  &__details-panel-inner {
    min-height: 0;
    overflow: hidden;
    padding: 0;
  }

  // Padding lives here — not on the grid child
  &__details-panel-content {
    padding: var(--spacing-md) var(--spacing-xl) var(--spacing-xl);

    @include desktop {
      padding: var(--spacing-md) var(--spacing-5xl) var(--spacing-2xl);
      max-width: px-to-rem(936px);
      margin: 0 auto;
      box-sizing: border-box;
    }

    me-text .me-text {
      font-size: px-to-rem(14px);
      line-height: 1.5;
      color: var(--text-color-alternate);
      text-align: left;
    }
  }

  // Toggle bar — no hover bg change
  &__details-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: px-to-rem(6px);
    width: 100%;
    padding: px-to-rem(24px) px-to-rem(10px);
    background: none;
    border: none;
    color: var(--text-color-alternate);
    cursor: pointer;
  }

  &__details-label {
    font-family: $uslf-font-family-base;
    font-size: px-to-rem(14px);
    font-weight: map-get($font-weights, bold);
    line-height: px-to-rem(24px);
    white-space: nowrap;
  }

  // Arrow icon — orange (#ff9000) to match Figma
  // Closed: chevron-down at 0deg = points DOWN (content slides up on click)
  // Open:   rotate 180deg = points UP (click to collapse)
  &__details-icon {
    // The chevron SVG is fetched lazily, so the icon box is empty (0x0) until it lands.
    // Reserve its final footprint up front — otherwise the centred label slides sideways
    // (measured: 12px) the moment the icon appears.
    @include size(24px);

    display: inline-flex;
    flex-shrink: 0;
    color: var(--color-brand-primary); // orange
    transform: rotate(0deg);
    transition: transform 0.3s ease;
  }

  // `me-icon` mirrors its `class` onto BOTH its host element and its inner `.me-icon`
  // wrapper, so both nested nodes carry `&__details-icon--open`. A bare
  // `&__details-icon--open { transform: rotate(180deg) }` therefore rotates the two nested
  // elements and the turns compound to 360° — which is why the arrow spun but always ended
  // up pointing down. Scope the open rotation to the host only (the me-icon is a direct
  // child of the toggle; its inner wrapper is not) so the chevron flips a single 180°.
  &__details-toggle > &__details-icon--open {
    transform: rotate(180deg);
  }
}

// When JS is disabled the panel must appear expanded.
// scripting:none is only true when the browser has JS turned off — JS-enabled
// browsers never match this rule, so the toggle behavior is completely unaffected.
@media (scripting: none) {
  .ul-hero-header-type-1__details-panel {
    grid-template-rows: 1fr;
    transition: none;
  }
}
